import React, {useEffect, useReducer, useState} from 'react';
import '@assets/fonts/styles.css';
import '@assets/css/style.css';
import dropdownReducer from '@reducers/dropdown-reducer';
import {useDispatch, useSelector} from 'react-redux';
import {useAuth} from '@hooks/use-auth';
import {activeDropdown} from '@redux/reducers/dropdown-reducer';
import {hideMenu, toggleMenu} from '@reducers/menu-reducer';
import defaultAvatar from '@assets/default-avatar/9.png';
import {useTranslation} from 'react-i18next';
import {MODAL_TYPES, useModal} from '@contexts/global-modal-context';
import {TERMS} from '@features/signup/terms/terms';
import {RootState} from '@redux/store';
import {Link} from 'react-router-dom';
import {capitalize} from '@utils/string.util';
import {useDeposit} from '@hooks/use-deposit';
import {SidebarType} from '@enums/sidebar.enum';
import {useSideBar} from '@hooks/use-sidebar';

// Notice here we're using the function declaration with the interface Props
const DashboardSidebar: React.FunctionComponent = () => {
    const {setSideBar} = useSideBar();
    const [dropdownState, dispatch] = useReducer(dropdownReducer, {active: false, element: 'home-menu'});
    const {opened} = useSelector((state: RootState) => state.menuStore);
    const _dispatch = useDispatch();
    const {games: pendingGames} = useSelector((state: RootState) => state.pendingGameStore);
    const {games: ongoingGames} = useSelector((state: RootState) => state.ongoingGameStore);
    const {tournaments} = useSelector((state: RootState) => state.tournamentStore);
    const [totalGames, setTotalGames] = useState<number>(0);
    const {user} = useAuth();
    const {t} = useTranslation(['sidebar']);
    const {showModal} = useModal();
    const {onDeposit} = useDeposit();

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const id: string = event.currentTarget?.parentElement?.id ?? '';
        const active = event.currentTarget.parentElement?.classList.contains('openned');

        dispatch(activeDropdown({id, active: !active}));
    };

    useEffect(() => {
        document.querySelectorAll('.link.drop-down.openned').forEach((currentOppened) => {
            currentOppened.classList.remove('openned');
        });
        if (dropdownState.active) {
            const currentOppened = document.getElementById(dropdownState.element);
            currentOppened?.classList.add('openned');
        }
    }, [dropdownState]);

    const handleOpenMenu = () => {
        _dispatch(toggleMenu());
    };

    const handleCloseMenu = () => {
        _dispatch(hideMenu());
    };

    const showTerms = () => {
        handleCloseMenu();
        showModal(MODAL_TYPES.TERMS_MODAL, {type: TERMS});
    };

    const showCosts = () => {
        handleCloseMenu();
        showModal(MODAL_TYPES.COSTS);
    };

    const showTips = () => {
        handleCloseMenu();
        showModal(MODAL_TYPES.TIPS, {isFirstUsage: false});
    };

    const showSettings = () => {
        handleCloseMenu();
        showModal(MODAL_TYPES.SETTINGS);
    };

    const handleWidrawal = () => {
        handleCloseMenu();
        showModal(MODAL_TYPES.WITHDRAWAL_MODAL, {});
    };

    useEffect(() => {
        setTotalGames(pendingGames.length + ongoingGames.length);
    }, [pendingGames, ongoingGames]);

    return (
        <div className={`left-side ${opened ? 'open' : ''}`}>
            <button className="close-sub-menu web-hide" onClick={handleOpenMenu}>
                <span className="icon icon12">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M15.7071 1.70711C16.0976 1.31658 16.0976 0.683417 15.7071 0.292893C15.3166 -0.0976311 14.6834 -0.0976311 14.2929 0.292893L15.7071 1.70711ZM0.292893 14.2929C-0.0976311 14.6834 -0.0976311 15.3166 0.292893 15.7071C0.683417 16.0976 1.31658 16.0976 1.70711 15.7071L0.292893 14.2929ZM1.70712 0.292893C1.3166 -0.0976311 0.683432 -0.0976311 0.292907 0.292893C-0.097617 0.683418 -0.097617 1.31658 0.292907 1.70711L1.70712 0.292893ZM14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0977 15.3166 16.0977 14.6834 15.7071 14.2929L14.2929 15.7071ZM14.2929 0.292893L0.292893 14.2929L1.70711 15.7071L15.7071 1.70711L14.2929 0.292893ZM0.292907 1.70711L14.2929 15.7071L15.7071 14.2929L1.70712 0.292893L0.292907 1.70711Z"
                            fill="white"
                        />
                    </svg>
                </span>
            </button>
            <div className="sub-menu">
                <div className="top-menu">
                    <div className="application">
                        <div className="app-logo">
                            <span className="icon icon32">
                                <svg
                                    width="32"
                                    height="25"
                                    viewBox="0 0 32 25"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M7.07534 3.74155L1.49876 5.77126C0.317109 6.20134 -0.292153 7.50791 0.137932 8.68956L5.48122 23.3701C5.88973 24.4925 7.08902 25.0985 8.22092 24.7879C7.51196 24.0685 7.07445 23.0809 7.07445 21.9911V3.82647C7.07445 3.79809 7.07475 3.76979 7.07534 3.74155Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M18.6681 1.49876C19.0982 0.317108 20.4048 -0.292152 21.5864 0.137932L30.5012 3.38267C31.6829 3.81275 32.2922 5.11932 31.8621 6.30097L25.6494 23.3701C25.2193 24.5518 23.9127 25.161 22.7311 24.731L13.8163 21.4862C12.6346 21.0561 12.0253 19.7496 12.4554 18.5679L18.6681 1.49876Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M16.8323 1.54963H11.059C9.80149 1.54963 8.7821 2.56902 8.7821 3.8265V21.9911C8.7821 23.2486 9.80149 24.268 11.059 24.268H16.4664L13.2322 23.0909C11.1643 22.3382 10.0981 20.0517 10.8508 17.9838L16.8323 1.54963Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M24.5901 10.0934C23.8386 9.8199 23.3088 10.0374 23.0053 10.255C22.913 9.89332 22.6475 9.38635 21.8976 9.1134C21.0006 8.78693 19.8179 9.03253 19.3408 10.3433C19.1528 10.8598 19.1157 11.3106 19.2168 11.8506C19.3153 12.3761 19.6631 13.1063 20.2506 14.0208C20.4799 14.3777 20.6981 14.6872 20.8408 14.884C21.1151 15.2622 21.1284 15.27 21.1723 15.286C21.1745 15.2869 21.1767 15.2877 21.1788 15.2884C21.2204 15.3036 21.2538 15.3007 21.6866 15.1935C21.9224 15.135 22.2885 15.0391 22.6937 14.9137C23.7321 14.5923 24.4698 14.256 24.8862 13.9141C25.31 13.5661 25.5712 13.1968 25.7595 12.6795C26.2366 11.3687 25.4878 10.4201 24.5901 10.0934Z"
                                        fill="#42BE65"
                                    />
                                </svg>
                            </span>
                        </div>

                        <div className="identity">
                            <span className="name">FapFap</span>
                            <span className="slogan">{t('Le meilleur terre')}</span>
                        </div>
                    </div>
                    <div className="toggle-switch">
                        <label>
                            <input type="checkbox"/>
                            <span className="slider">
                                <span className="dark icon12 icon">
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M1 10.3333L1.19934 9.3534C0.800488 9.27227 0.392254 9.44097 0.167037 9.78001C-0.0581808 10.119 -0.0554377 10.5608 0.173973 10.897L1 10.3333ZM6.33333 1L6.35961 0.00034529C5.95169 -0.0103768 5.57818 0.227825 5.41586 0.602207C5.25354 0.976589 5.33497 1.41204 5.62161 1.70246L6.33333 1ZM13.3333 7C13.3333 3.17329 10.2093 0.101533 6.35961 0.00034529L6.30706 1.99965C9.12501 2.07372 11.3333 4.30869 11.3333 7H13.3333ZM6.33333 14C10.3089 14 13.3333 10.8559 13.3333 7H11.3333C11.3333 9.7728 9.18291 12 6.33333 12V14ZM0.173973 10.897C1.48441 12.8175 3.88325 14 6.33333 14V12C4.47823 12 2.72809 11.0917 1.82603 9.7697L0.173973 10.897ZM0.800657 11.3133C3.93807 11.9515 6.61811 10.31 7.94212 8.0093C9.26931 5.70306 9.30986 2.59218 7.04505 0.297535L5.62161 1.70246C7.1454 3.24632 7.16995 5.34132 6.20867 7.01173C5.2442 8.68767 3.36329 9.7936 1.19934 9.3534L0.800657 11.3133Z"
                                            fill="#212121"
                                        />
                                    </svg>
                                </span>
                                <span className="light icon icon12">
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M2 7.25C1.58579 7.25 1.25 7.58579 1.25 8C1.25 8.41421 1.58579 8.75 2 8.75V7.25ZM3.33333 8.75C3.74755 8.75 4.08333 8.41421 4.08333 8C4.08333 7.58579 3.74755 7.25 3.33333 7.25V8.75ZM2.80304 12.1363C2.51015 12.4292 2.51015 12.9041 2.80304 13.197C3.09594 13.4899 3.57081 13.4899 3.8637 13.197L2.80304 12.1363ZM5.19704 11.8637C5.48993 11.5708 5.48993 11.0959 5.19704 10.803C4.90414 10.5101 4.42927 10.5101 4.13638 10.803L5.19704 11.8637ZM8.75 12.6667C8.75 12.2525 8.41421 11.9167 8 11.9167C7.58579 11.9167 7.25 12.2525 7.25 12.6667H8.75ZM7.25 14C7.25 14.4142 7.58579 14.75 8 14.75C8.41421 14.75 8.75 14.4142 8.75 14H7.25ZM11.8637 10.803C11.5708 10.5101 11.0959 10.5101 10.803 10.803C10.5101 11.0959 10.5101 11.5708 10.803 11.8637L11.8637 10.803ZM12.1363 13.197C12.4292 13.4899 12.9041 13.4899 13.197 13.197C13.4899 12.9041 13.4899 12.4292 13.197 12.1363L12.1363 13.197ZM3.86366 2.803C3.57077 2.51011 3.0959 2.51011 2.803 2.803C2.51011 3.0959 2.51011 3.57077 2.803 3.86366L3.86366 2.803ZM4.13634 5.197C4.42923 5.48989 4.9041 5.48989 5.197 5.197C5.48989 4.9041 5.48989 4.42923 5.197 4.13634L4.13634 5.197ZM12.6667 7.25C12.2525 7.25 11.9167 7.58579 11.9167 8C11.9167 8.41421 12.2525 8.75 12.6667 8.75V7.25ZM14 8.75C14.4142 8.75 14.75 8.41421 14.75 8C14.75 7.58579 14.4142 7.25 14 7.25V8.75ZM10.803 4.13634C10.5101 4.42923 10.5101 4.9041 10.803 5.197C11.0959 5.48989 11.5707 5.48989 11.8636 5.197L10.803 4.13634ZM13.197 3.86366C13.4899 3.57077 13.4899 3.0959 13.197 2.803C12.9041 2.51011 12.4292 2.51011 12.1363 2.803L13.197 3.86366ZM8.75 2C8.75 1.58579 8.41421 1.25 8 1.25C7.58579 1.25 7.25 1.58579 7.25 2H8.75ZM7.25 3.33333C7.25 3.74755 7.58579 4.08333 8 4.08333C8.41421 4.08333 8.75 3.74755 8.75 3.33333H7.25ZM9.25 8C9.25 8.69036 8.69036 9.25 8 9.25V10.75C9.51878 10.75 10.75 9.51878 10.75 8H9.25ZM8 9.25C7.30964 9.25 6.75 8.69036 6.75 8H5.25C5.25 9.51878 6.48122 10.75 8 10.75V9.25ZM6.75 8C6.75 7.30964 7.30964 6.75 8 6.75V5.25C6.48122 5.25 5.25 6.48122 5.25 8H6.75ZM8 6.75C8.69036 6.75 9.25 7.30964 9.25 8H10.75C10.75 6.48122 9.51878 5.25 8 5.25V6.75ZM2 8.75H3.33333V7.25H2V8.75ZM3.8637 13.197L5.19704 11.8637L4.13638 10.803L2.80304 12.1363L3.8637 13.197ZM7.25 12.6667V14H8.75V12.6667H7.25ZM10.803 11.8637L12.1363 13.197L13.197 12.1363L11.8637 10.803L10.803 11.8637ZM2.803 3.86366L4.13634 5.197L5.197 4.13634L3.86366 2.803L2.803 3.86366ZM12.6667 8.75H14V7.25H12.6667V8.75ZM11.8636 5.197L13.197 3.86366L12.1363 2.803L10.803 4.13634L11.8636 5.197ZM7.25 2V3.33333H8.75V2H7.25Z"
                                            fill="white"
                                        />
                                    </svg>
                                </span>
                            </span>
                        </label>
                    </div>
                </div>
                <div className="menu-links">
                    <ul className="links">
                        <li className="link" id="play-menu">
                            <div className="link-identification">
                                <Link to="/apps/games/list" className="link-label play-label" onClick={handleOpenMenu}>
                                    <span className="link-icon icon icon16">
                                        <svg
                                            width="19"
                                            height="26"
                                            viewBox="0 0 19 26"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M8.16551 13.0154V15.9551H10.9505V16.574H8.16551V19.5136H7.54663V16.574H4.76168V15.9551H7.54663V13.0154H8.16551Z"
                                                stroke="#212121"
                                                strokeWidth="1"
                                                fill="none"
                                            ></path>
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M3.26501 5.53478L3.71916 7.67139H3.67864C2.31145 7.67139 1.20312 8.77972 1.20312 10.1469V22.5245C1.20312 23.8917 2.31145 25 3.67864 25H11.1052C12.4724 25 13.5807 23.8917 13.5807 22.5245V18.527L16.0388 18.0045C17.3761 17.7203 18.2298 16.4057 17.9455 15.0684L15.3721 2.96133C15.0878 1.62402 13.7733 0.770349 12.436 1.0546L5.17174 2.59867C3.83443 2.88292 2.98076 4.19747 3.26501 5.53478ZM12.5647 1.65996L5.30041 3.20402C4.29743 3.41722 3.65717 4.40312 3.87036 5.40611L4.35187 7.67139H11.5336C12.7299 7.67139 13.6997 8.64118 13.6997 9.83747V17.869L15.9101 17.3992C16.9131 17.186 17.5534 16.2001 17.3402 15.1971L14.7667 3.09001C14.5536 2.08702 13.5677 1.44677 12.5647 1.65996ZM3.67864 8.29027H11.1052C12.1306 8.29027 12.9618 9.12152 12.9618 10.1469V22.5245C12.9618 23.5499 12.1306 24.3811 11.1052 24.3811H3.67864C2.65325 24.3811 1.822 23.5499 1.822 22.5245V10.1469C1.822 9.12152 2.65325 8.29027 3.67864 8.29027Z"
                                                stroke="#212121"
                                                strokeWidth="1"
                                                fill="none"
                                            ></path>
                                            <path
                                                d="M3.71916 7.67139V8.17139H4.33661L4.20824 7.56744L3.71916 7.67139ZM3.26501 5.53478L2.77594 5.63873L3.26501 5.53478ZM13.5807 18.527L13.4767 18.038L13.0807 18.1221V18.527H13.5807ZM16.0388 18.0045L15.9349 17.5155H15.9349L16.0388 18.0045ZM17.9455 15.0684L18.4346 14.9645V14.9645L17.9455 15.0684ZM15.3721 2.96133L15.8612 2.85738V2.85738L15.3721 2.96133ZM12.436 1.0546L12.54 1.54368L12.436 1.0546ZM5.17174 2.59867L5.06778 2.1096H5.06778L5.17174 2.59867ZM5.30041 3.20402L5.19646 2.71495H5.19646L5.30041 3.20402ZM12.5647 1.65996L12.6686 2.14903L12.5647 1.65996ZM3.87036 5.40611L3.38129 5.51006L3.87036 5.40611ZM4.35187 7.67139L3.86279 7.77535L3.94697 8.17139H4.35187V7.67139ZM11.5336 7.67139V7.17139V7.67139ZM13.6997 17.869H13.1997V18.4865L13.8036 18.3581L13.6997 17.869ZM15.9101 17.3992L15.8062 16.9101H15.8062L15.9101 17.3992ZM17.3402 15.1971L17.8293 15.0931V15.0931L17.3402 15.1971ZM14.7667 3.09001L15.2558 2.98605V2.98605L14.7667 3.09001Z"
                                                stroke="#212121"
                                                strokeWidth="1"
                                                fill="none"
                                            ></path>
                                        </svg>
                                    </span>
                                    <span className="d-flex w-100 align-items-center justify-content-between">
                                        <span className="link-name">{t('play')}</span>
                                        {totalGames > 0 && <span className="notif-number">{totalGames}</span>}
                                    </span>
                                </Link>
                            </div>
                        </li>
                        <li className="link" id="play-menu">
                            <div className="link-identification">
                                <Link
                                    to="/apps/games/competitions"
                                    className="link-label play-label"
                                    onClick={handleOpenMenu}
                                >
                                    <span className="link-icon icon icon16">
                                        <svg
                                            fill="currentColor"
                                            height="12px"
                                            width="12px"
                                            version="1.1"
                                            id="Layer_1"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                        >
                                            <g>
                                                <g>
                                                    <path
                                                        d="M363.232,271.105c-3.463-2.757-8.508-2.183-11.264,1.281c-2.885,3.626-6.016,7.132-9.306,10.42
			c-6.794,6.795-14.348,12.765-22.451,17.746c-3.772,2.317-4.951,7.256-2.633,11.027c1.514,2.464,4.144,3.82,6.837,3.82
			c1.432,0,2.881-0.384,4.19-1.188c9.17-5.636,17.714-12.387,25.394-20.067c3.717-3.716,7.252-7.676,10.514-11.774
			C367.272,278.906,366.698,273.863,363.232,271.105z"
                                                    />
                                                </g>
                                            </g>
                                            <g>
                                                <g>
                                                    <path
                                                        d="M386.405,188.126c-4.428,0-8.017,3.589-8.017,8.017c0,16.794-3.265,33.003-9.706,48.174
			c-1.729,4.076,0.172,8.782,4.248,10.513c1.023,0.434,2.084,0.639,3.13,0.639c3.12,0,6.086-1.833,7.382-4.886
			c7.286-17.164,10.98-35.481,10.98-54.44C394.422,191.715,390.833,188.126,386.405,188.126z"
                                                    />
                                                </g>
                                            </g>
                                            <g>
                                                <g>
                                                    <path
                                                        d="M439.85,175.299c-4.506,0-8.97,0.432-13.361,1.258v-15.154h17.637c4.428,0,8.017-3.589,8.017-8.017v-47.031
			c0-4.427-3.588-8.017-8.017-8.017h-25.653H93.528H67.875c-4.428,0-8.017,3.589-8.017,8.017v47.031
			c0,4.427,3.588,8.017,8.017,8.017h17.637v15.154c-4.391-0.826-8.855-1.258-13.361-1.258c-39.784,0-72.15,32.366-72.15,72.15
			s32.366,72.15,72.15,72.15c18.295,0,35.606-6.87,48.848-19.087c26.11,33.787,63.865,56.311,105.607,63.568v70.283
			c-38.349,6.652-73.374,26.285-99.25,55.782c-2.076,2.365-2.575,5.727-1.276,8.594c1.3,2.867,4.156,4.708,7.303,4.708h245.236
			c3.147,0,6.003-1.842,7.303-4.708c1.299-2.867,0.799-6.228-1.276-8.594c-25.876-29.497-60.9-49.131-99.25-55.782v-70.283
			c41.742-7.257,79.497-29.782,105.607-63.568c13.243,12.217,30.553,19.087,48.848,19.087c39.784,0,72.15-32.366,72.15-72.15
			S479.634,175.299,439.85,175.299z M75.891,145.37v-30.998h17.637h324.944h17.637v30.998h-17.637H93.528H75.891z M439.85,225.537
			c12.083,0,21.912,9.83,21.912,21.912s-9.83,21.912-21.912,21.912c-11.476,0-20.808-8.678-21.793-19.965
			c1.572-4.825,2.948-9.807,4.101-14.862C426.288,228.878,432.808,225.537,439.85,225.537z M93.942,249.397
			c-0.983,11.286-10.316,19.965-21.792,19.965c-12.083,0-21.912-9.83-21.912-21.912s9.83-21.912,21.912-21.912
			c7.042,0,13.562,3.342,17.69,8.998C90.995,239.59,92.37,244.571,93.942,249.397z M72.15,303.566
			c-30.942,0-56.117-25.174-56.117-56.117s25.175-56.117,56.117-56.117c4.527,0,9,0.552,13.361,1.62v3.19
			c0,5.369,0.266,10.737,0.764,16.079c-4.419-1.769-9.197-2.718-14.125-2.718c-20.924,0-37.946,17.022-37.946,37.946
			s17.022,37.946,37.946,37.946c12.388,0,23.346-5.839,30.261-14.969c2.78,5.762,5.875,11.376,9.298,16.804
			C101.229,297.669,87.108,303.566,72.15,303.566z M359.429,487.416H152.57c23.169-20.763,52.084-34.153,83.094-38.216
			c3.99-0.523,6.975-3.924,6.975-7.949v-74.619h26.723v74.619c0,4.025,2.984,7.426,6.975,7.949
			C307.346,453.263,336.261,466.653,359.429,487.416z M383.578,283.487c-0.003,0.003-0.005,0.006-0.007,0.011
			c-28.745,42.016-76.434,67.1-127.57,67.1s-98.826-25.084-127.571-67.1c-0.002-0.003-0.005-0.006-0.008-0.011
			c-7.966-11.646-14.281-24.299-18.771-37.61c-1.774-5.262-3.293-10.762-4.514-16.347c0-0.002-0.001-0.004-0.002-0.006
			c-2.381-10.919-3.589-22.151-3.589-33.382v-9.163v-25.576h308.91v25.576v9.163c0,11.231-1.208,22.463-3.59,33.382
			c0,0.002-0.001,0.004-0.002,0.006c-1.221,5.585-2.74,11.084-4.514,16.347C397.859,259.188,391.544,271.842,383.578,283.487z
			 M439.85,303.566c-14.958,0-29.078-5.897-39.559-16.336c3.424-5.428,6.518-11.042,9.298-16.804
			c6.915,9.129,17.872,14.969,30.26,14.969c20.924,0,37.946-17.022,37.946-37.946s-17.022-37.946-37.946-37.946
			c-4.929,0-9.707,0.949-14.125,2.718c0.498-5.342,0.764-10.71,0.764-16.079v-3.19c4.362-1.07,8.834-1.62,13.361-1.62
			c30.942,0,56.117,25.173,56.117,56.117S470.792,303.566,439.85,303.566z"
                                                    />
                                                </g>
                                            </g>
                                            <g>
                                                <g>
                                                    <path
                                                        d="M331.978,231.981c-1.123-3.218-4.159-5.375-7.569-5.375h-45.692l-15.183-41.754c-1.152-3.168-4.163-5.277-7.535-5.277
			s-6.382,2.109-7.535,5.277l-15.183,41.754h-45.692c-3.409,0-6.445,2.157-7.569,5.375c-1.123,3.219-0.088,6.797,2.582,8.918
			l38.617,30.68l-11.435,41.933c-0.909,3.329,0.416,6.866,3.287,8.779c2.871,1.915,6.646,1.778,9.369-0.342l33.558-26.1l33.559,26.1
			c1.443,1.122,3.181,1.689,4.922,1.689c1.546,0,3.096-0.446,4.445-1.347c2.872-1.914,4.195-5.45,3.287-8.779l-11.435-41.933
			l38.617-30.68C332.066,238.779,333.101,235.2,331.978,231.981z M276.667,262.313c-2.511,1.995-3.591,5.293-2.747,8.386
			l6.498,23.829l-19.496-15.163c-1.448-1.126-3.185-1.689-4.922-1.689c-1.737,0-3.474,0.563-4.922,1.689l-19.496,15.163
			l6.498-23.829c0.844-3.093-0.237-6.392-2.747-8.387l-24.764-19.674h28.329c3.37,0,6.382-2.109,7.535-5.277L256,211.049
			l9.568,26.313c1.152,3.168,4.163,5.277,7.535,5.277h28.329L276.667,262.313z"
                                                    />
                                                </g>
                                            </g>
                                            <g>
                                                <g>
                                                    <path
                                                        d="M277.378,29.93h-13.361V16.568c0-4.427-3.588-8.017-8.017-8.017c-4.428,0-8.017,3.589-8.017,8.017V29.93h-13.361
			c-4.428,0-8.017,3.589-8.017,8.017c0,4.427,3.588,8.017,8.017,8.017h13.361v13.361c0,4.427,3.588,8.017,8.017,8.017
			c4.428,0,8.017-3.589,8.017-8.017V45.963h13.361c4.428,0,8.017-3.589,8.017-8.017C285.395,33.519,281.806,29.93,277.378,29.93z"
                                                    />
                                                </g>
                                            </g>
                                            <g>
                                                <g>
                                                    <path
                                                        d="M397.094,29.93h-13.361V16.568c0-4.427-3.588-8.017-8.017-8.017c-4.428,0-8.017,3.589-8.017,8.017V29.93h-13.361
			c-4.428,0-8.017,3.589-8.017,8.017c0,4.427,3.588,8.017,8.017,8.017h13.361v13.361c0,4.427,3.588,8.017,8.017,8.017
			c4.428,0,8.017-3.589,8.017-8.017V45.963h13.361c4.428,0,8.017-3.589,8.017-8.017C405.111,33.519,401.522,29.93,397.094,29.93z"
                                                    />
                                                </g>
                                            </g>
                                            <g>
                                                <g>
                                                    <path
                                                        d="M166.213,29.93h-13.361V16.568c0-4.427-3.588-8.017-8.017-8.017c-4.428,0-8.017,3.589-8.017,8.017V29.93h-13.361
			c-4.428,0-8.017,3.589-8.017,8.017c0,4.427,3.588,8.017,8.017,8.017h13.361v13.361c0,4.427,3.588,8.017,8.017,8.017
			c4.428,0,8.017-3.589,8.017-8.017V45.963h13.361c4.428,0,8.017-3.589,8.017-8.017C174.23,33.519,170.641,29.93,166.213,29.93z"
                                                    />
                                                </g>
                                            </g>
                                        </svg>
                                    </span>
                                    <span className="d-flex w-100 align-items-center justify-content-between">
                                        <span className="link-name d-flex align-items-center gap-1">
                                            {t('competition_event')} <span className="notif-new">{t('new')}</span>
                                        </span>
                                        {tournaments.length > 0 && (
                                            <span className="notif-number">{tournaments.length}</span>
                                        )}
                                    </span>
                                </Link>
                            </div>
                        </li>
                        <li className="link drop-down" id="home-menu">
                            <div className="link-identification" onClick={handleClick}>
                                <div className="link-label">
                                    <span className="link-icon icon icon24">
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M5 12.9989H9.00114M5 13.0003V11.0006C5 9.89569 5.89569 9 7.00057 9C8.10546 9 9.00114 9.89569 9.00114 11.0006V13.0003L11.0022 13C11.8698 12.9911 12.6051 12.359 12.744 11.5025L12.9754 5.28053C13.1132 4.49801 12.7273 3.71658 12.0222 3.35024L7.79224 1.1911C7.29589 0.9363 6.70716 0.9363 6.21081 1.1911L1.98081 3.35024C1.27576 3.71658 0.889899 4.49801 1.02767 5.28053L1.25824 11.5025C1.3971 12.359 2.13236 12.9911 2.99996 13L5 13.0003Z"
                                                stroke="#212121"
                                                strokeOpacity="0.48"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>
                                    <span className="link-name">{t('my_account')}</span>
                                </div>
                                <div className="drop-down-trigger">
                                    <span className="icon icon24">
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L7.70711 9.29289ZM12 15L11.2929 15.7071C11.6834 16.0976 12.3166 16.0976 12.7071 15.7071L12 15ZM17.7071 10.7071C18.0976 10.3166 18.0976 9.68342 17.7071 9.29289C17.3166 8.90237 16.6834 8.90237 16.2929 9.29289L17.7071 10.7071ZM6.29289 10.7071L11.2929 15.7071L12.7071 14.2929L7.70711 9.29289L6.29289 10.7071ZM12.7071 15.7071L17.7071 10.7071L16.2929 9.29289L11.2929 14.2929L12.7071 15.7071Z"
                                                fill="white"
                                                fillOpacity="0.48"
                                            />
                                        </svg>
                                    </span>
                                </div>
                            </div>
                            <ul className="dropdown-sublevel">
                                <li>
                                    <div
                                        className="link-identification"
                                        id="profile-menu"
                                        onClick={() => setSideBar(SidebarType.PROFILE)}
                                    >
                                        <div className="link-label">
                                            <span className="link-name"> {t('profile')} </span>
                                        </div>
                                    </div>
                                </li>
                                <li>
                                    {' '}
                                    <a onClick={() => onDeposit()}> {t('Recharger')} </a>{' '}
                                </li>
                                <li>
                                    {' '}
                                    <a onClick={handleWidrawal}> {t('Décaisser')} </a>{' '}
                                </li>
                                <li>
                                    <Link to="/apps/history" onClick={handleOpenMenu}>
                                        {t('history_menu', {ns: 'transaction'})}
                                    </Link>
                                </li>
                            </ul>
                        </li>
                        <li className="link drop-down" id="knowlage-menu">
                            <div className="link-identification" onClick={handleClick}>
                                <div className="link-label">
                                    <span className="link-icon icon icon16">
                                        <svg
                                            width="22"
                                            height="18"
                                            viewBox="0 0 22 18"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.9999 3.39421C9.04379 1.20172 5.94698 0.426463 3.18862 1.43873C2.24948 1.78589 1.64099 2.69863 1.68175 3.69904V13.2328C1.73834 14.12 2.16923 14.9413 2.86701 15.4921C3.5648 16.0428 4.46374 16.2711 5.33978 16.1201C7.42304 15.8312 9.68868 15.8978 10.9999 17M10.9999 3.39421V17M10.9999 3.39421C12.9559 1.20172 16.053 0.426463 18.8114 1.43873C19.7505 1.78589 20.359 2.69863 20.3183 3.69904V13.2328C20.2617 14.12 19.8308 14.9413 19.133 15.4921C18.4352 16.0428 17.5363 16.2711 16.6602 16.1201C14.577 15.8312 12.3111 15.8978 10.9999 17M7.67124 7.68023H5.00892M7.67124 10.3443H5.00892M16.9896 7.68023H14.3273M16.9896 10.3443H14.3273"
                                                stroke="white"
                                                strokeOpacity="0.48"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>
                                    <span className="link-name"> {t('Bon a savoir')} </span>
                                </div>
                                <div className="drop-down-trigger">
                                    <span className="icon icon16">
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L7.70711 9.29289ZM12 15L11.2929 15.7071C11.6834 16.0976 12.3166 16.0976 12.7071 15.7071L12 15ZM17.7071 10.7071C18.0976 10.3166 18.0976 9.68342 17.7071 9.29289C17.3166 8.90237 16.6834 8.90237 16.2929 9.29289L17.7071 10.7071ZM6.29289 10.7071L11.2929 15.7071L12.7071 14.2929L7.70711 9.29289L6.29289 10.7071ZM12.7071 15.7071L17.7071 10.7071L16.2929 9.29289L11.2929 14.2929L12.7071 15.7071Z"
                                                fill="white"
                                                fillOpacity="0.48"
                                            />
                                        </svg>
                                    </span>
                                </div>
                            </div>

                            <ul className="dropdown-sublevel">
                                <li>
                                    {' '}
                                    <Link to="/playing">{t('Apprendre à jouer')}</Link>{' '}
                                </li>
                                <li>
                                    {' '}
                                    <a onClick={showCosts}> {t('Frais applicables')} </a>{' '}
                                </li>
                                <li>
                                    {' '}
                                    <a onClick={showTerms}>
                                        {t("Conditions d'utilisation & Politique de confidentialité")}
                                    </a>{' '}
                                </li>
                                <li>
                                    <a onClick={showTips}> {t('Astuces')} </a>
                                </li>
                            </ul>
                        </li>
                        <li className="link drop-down" id="community-menu">
                            <div className="link-identification" onClick={handleClick}>
                                <div className="link-label">
                                    <span className="link-icon icon icon16">
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 18 18"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M2.32271 7.63379C3.05934 7.65514 3.64542 8.25845 3.64542 8.99539C3.64542 9.73233 3.05934 10.3356 2.32271 10.357C1.58608 10.3356 1 9.73233 1 8.99539C1 8.25845 1.58608 7.65514 2.32271 7.63379Z"
                                                stroke="white"
                                                strokeOpacity="0.48"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M15.6651 10.355C15.1116 10.3661 14.6065 10.0413 14.3871 9.53312C14.1676 9.02495 14.2775 8.43452 14.665 8.03932C15.0526 7.64412 15.6408 7.52274 16.1531 7.73223C16.6655 7.94172 17.0001 8.44042 16.9998 8.99394C17.0068 9.73824 16.4093 10.3474 15.6651 10.355Z"
                                                stroke="white"
                                                strokeOpacity="0.48"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M7.63211 15.6651C7.62096 15.1116 7.9458 14.6065 8.45397 14.3871C8.96214 14.1676 9.55257 14.2775 9.94777 14.665C10.343 15.0526 10.4644 15.6408 10.2549 16.1531C10.0454 16.6655 9.54667 17.0001 8.99314 16.9998C8.24885 17.0068 7.63966 16.4093 7.63211 15.6651Z"
                                                stroke="white"
                                                strokeOpacity="0.48"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M10.355 2.32271C10.3337 3.05934 9.73038 3.64542 8.99344 3.64542C8.2565 3.64542 7.65319 3.05934 7.63184 2.32271C7.65319 1.58608 8.2565 1 8.99344 1C9.73038 1 10.3337 1.58608 10.355 2.32271Z"
                                                stroke="white"
                                                strokeOpacity="0.48"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M10.7731 8.9935C10.7731 9.97637 9.97637 10.7731 8.9935 10.7731C8.01064 10.7731 7.21387 9.97637 7.21387 8.9935C7.21387 8.01064 8.01064 7.21387 8.9935 7.21387C9.97637 7.21387 10.7731 8.01064 10.7731 8.9935Z"
                                                stroke="white"
                                                strokeOpacity="0.48"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>
                                    <span className="link-name"> {t('Communauté FapFap')} </span>
                                </div>
                                <div className="drop-down-trigger">
                                    <span className="icon icon16">
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L7.70711 9.29289ZM12 15L11.2929 15.7071C11.6834 16.0976 12.3166 16.0976 12.7071 15.7071L12 15ZM17.7071 10.7071C18.0976 10.3166 18.0976 9.68342 17.7071 9.29289C17.3166 8.90237 16.6834 8.90237 16.2929 9.29289L17.7071 10.7071ZM6.29289 10.7071L11.2929 15.7071L12.7071 14.2929L7.70711 9.29289L6.29289 10.7071ZM12.7071 15.7071L17.7071 10.7071L16.2929 9.29289L11.2929 14.2929L12.7071 15.7071Z"
                                                fill="white"
                                                fillOpacity="0.48"
                                            />
                                        </svg>
                                    </span>
                                </div>
                            </div>

                            <ul className="dropdown-sublevel">
                                <li>
                                    {' '}
                                    <a href="https://t.me/joinchat/Q_vnw1XQAuSmaa-hShNTCQ">
                                        {' '}
                                        {t('Rejoindre la communauté')}{' '}
                                    </a>{' '}
                                </li>
                                <li>
                                    {' '}
                                    <Link to="/contact-us"> {t('Nous contacter')} </Link>{' '}
                                </li>
                            </ul>
                        </li>
                        <li className="link drop-down">
                            <div className="link-identification" id="parameters-menu" onClick={showSettings}>
                                <div className="link-label">
                                    <span className="link-icon icon icon16">
                                        <svg
                                            width="17"
                                            height="18"
                                            viewBox="0 0 17 18"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M6.16669 3.15385L6.69592 1.78769C6.78518 1.55639 6.94221 1.35743 7.14645 1.21688C7.35068 1.07632 7.59261 1.00073 7.84054 1H8.84977C9.0977 1.00073 9.33962 1.07632 9.54386 1.21688C9.7481 1.35743 9.90513 1.55639 9.99439 1.78769L10.5236 3.15385L12.3205 4.18769L13.7728 3.96615C14.0147 3.93333 14.2608 3.97314 14.4799 4.08052C14.6991 4.1879 14.8814 4.35801 15.0036 4.56923L15.4959 5.43077C15.6221 5.64535 15.6802 5.89314 15.6626 6.14144C15.645 6.38974 15.5526 6.62687 15.3975 6.82154L14.499 7.96615V10.0338L15.4221 11.1785C15.5772 11.3731 15.6697 11.6103 15.6872 11.8586C15.7048 12.1069 15.6467 12.3546 15.5205 12.5692L15.0282 13.4308C14.906 13.642 14.7237 13.8121 14.5046 13.9195C14.2854 14.0269 14.0393 14.0667 13.7975 14.0338L12.3452 13.8123L10.5482 14.8462L10.019 16.2123C9.92975 16.4436 9.77272 16.6426 9.56848 16.7831C9.36424 16.9237 9.12231 16.9993 8.87438 17H7.84054C7.59261 16.9993 7.35068 16.9237 7.14645 16.7831C6.94221 16.6426 6.78518 16.4436 6.69592 16.2123L6.16669 14.8462L4.36977 13.8123L2.91746 14.0338C2.67564 14.0667 2.42951 14.0269 2.21036 13.9195C1.99121 13.8121 1.80894 13.642 1.68669 13.4308L1.19438 12.5692C1.06823 12.3546 1.01011 12.1069 1.02769 11.8586C1.04527 11.6103 1.13772 11.3731 1.29285 11.1785L2.19131 10.0338V7.96615L1.26823 6.82154C1.11311 6.62687 1.02065 6.38974 1.00307 6.14144C0.985495 5.89314 1.04362 5.64535 1.16977 5.43077L1.66208 4.56923C1.78432 4.35801 1.9666 4.1879 2.18575 4.08052C2.4049 3.97314 2.65102 3.93333 2.89285 3.96615L4.34515 4.18769L6.16669 3.15385ZM5.88362 9C5.88362 9.48685 6.02798 9.96276 6.29846 10.3676C6.56894 10.7724 6.95338 11.0879 7.40316 11.2742C7.85295 11.4605 8.34788 11.5092 8.82538 11.4142C9.30287 11.3193 9.74147 11.0848 10.0857 10.7406C10.43 10.3963 10.6644 9.95771 10.7594 9.48022C10.8544 9.00273 10.8056 8.5078 10.6193 8.05801C10.433 7.60822 10.1175 7.22378 9.71271 6.95331C9.30791 6.68283 8.832 6.53846 8.34515 6.53846C7.69231 6.53846 7.06621 6.7978 6.60458 7.25943C6.14296 7.72106 5.88362 8.34716 5.88362 9V9Z"
                                                stroke="white"
                                                strokeOpacity="0.5"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </span>
                                    <span className="link-name"> {t('Parametres')} </span>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
                <div className="user-menu">
                    <div onClick={() => setSideBar(SidebarType.PROFILE)} className="user">
                        <div className="user-avatar">
                            <img src={defaultAvatar} alt=""/>
                        </div>
                        <div className="user-id">
                            <span className="user-name">{capitalize(user?.pseudo)}</span>
                            <div className="sub-infos">
                                <span className="user-pseudo">@{user?.pseudo}</span>
                                <span className="user-sold">
                                    {user?.balance ?? 0}
                                    <span className="currency">XAF</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <span className="top-menu-dropdown">
                        <span className="icon icon16">
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L7.70711 9.29289ZM12 15L11.2929 15.7071C11.6834 16.0976 12.3166 16.0976 12.7071 15.7071L12 15ZM17.7071 10.7071C18.0976 10.3166 18.0976 9.68342 17.7071 9.29289C17.3166 8.90237 16.6834 8.90237 16.2929 9.29289L17.7071 10.7071ZM6.29289 10.7071L11.2929 15.7071L12.7071 14.2929L7.70711 9.29289L6.29289 10.7071ZM12.7071 15.7071L17.7071 10.7071L16.2929 9.29289L11.2929 14.2929L12.7071 15.7071Z"
                                    fill="white"
                                    fillOpacity="0.48"
                                />
                            </svg>
                        </span>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default DashboardSidebar;
